/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { ICartAffiliationsMaintainerData } from './cart-affiliations-maintainer.data';
import { ICartAffiliationsMaintainerProps } from './cart-affiliations-maintainer.props.autogenerated';

import { BaseCartState, GlobalStateInput, ICartState } from '@msdyn365-commerce/global-state';

export interface ICartAffiliationsMaintainerViewProps extends ICartAffiliationsMaintainerProps<ICartAffiliationsMaintainerData> {

}

type AffiliationCache = number[];

/**
 *
 * CartAffiliationsMaintainer component
 * @extends {React.PureComponent<ICartAffiliationsMaintainerProps<ICartAffiliationsMaintainerData>>}
 */
class CartAffiliationsMaintainer extends React.Component<ICartAffiliationsMaintainerProps<ICartAffiliationsMaintainerData>> {

    private static readonly AFFILIATION_LOCATION: string = 'CACHED_AFFIL';

    public componentDidMount(): void {
        var isLoggedIn = this.props.context.request.user.customerAccountNumber &&
            this.props.context.request.user.isAuthenticated;
        // tslint:disable-next-line: no-typeof-undefined
        if (typeof window !== 'undefined' && isLoggedIn) {
            void this._runAffiliationCheck();
        }
    }

    public render(): JSX.Element | null {
        return <div />;
    }

    private async _runAffiliationCheck(): Promise<void> {
        const customer = await this.props.data.customer;
        const currentAffils = customer.CustomerAffiliations || [];

        const cachedAffils = this._cachedAffiliations;

        // check if affiliations
        if (currentAffils.length !== cachedAffils.length) {
            await this._cacheAffiliations();
            return this._updateCart();
        }

        for (const curAffil of currentAffils) {
            const isInCache = cachedAffils.find(cachedAffil => curAffil.RecordId === cachedAffil);
            if (!isInCache) {
                await this._cacheAffiliations();
                return this._updateCart();
            }
        }
    }

    private async _updateCart(): Promise<void> {
        const cart = await this.props.data.cart;

        // if no cart items, no point
        if (cart.totalItemsInCart === 0) { return; }

        const firstLine = cart.cart.CartLines?.find(line => !!line)!;
        await cart.updateCartLineQuantity({
            cartLineId: firstLine.LineId!,
            newQuantity: firstLine.Quantity!
        });

        // @ts-ignore
        cart.isInitialized = false;
        await cart.initialize();

        // taken from addtocart.component.tsx:updateCart()
        setTimeout(async () => {
            const context = this.props.context;
            const cartInput = new GlobalStateInput<ICartState>('CARTSTATE', BaseCartState, context.actionContext.requestContext.apiSettings);
            context.actionContext.update(cartInput, cart);
            await cart.refreshCart({});
        }, 10);
    }

    private async _cacheAffiliations(): Promise<void> {
        const customer = await this.props.data.customer;
        const affils = customer.CustomerAffiliations || [];

        const affilIds = affils.map(affil => affil.RecordId);

        window.localStorage.setItem(
            CartAffiliationsMaintainer.AFFILIATION_LOCATION,
            JSON.stringify(affilIds)
        );
    }

    private get _cachedAffiliations(): AffiliationCache {
        const affiliationStr = window.localStorage.getItem(
            CartAffiliationsMaintainer.AFFILIATION_LOCATION
        ) || '[]';

        return JSON.parse(affiliationStr) as AffiliationCache;
    }
}

export default CartAffiliationsMaintainer;
